import { useCallback, useReducer } from 'react'

// State & actions
// -------------------------------------------------------------------------- /

// `extendCount` is the number of completed "Zoek verder" extensions (0 = only
// the initial search window has been covered). The component derives the
// searched date window from it; the 18-month ceiling is enforced there.
export type ExtendedDoctorSearchPhase =
  | { kind: 'idle'; extendCount: number }
  | { kind: 'searching'; endLabel: string; extendCount: number }
  | { kind: 'search-settled'; endLabel: string; extendCount: number }

type Action = { type: 'START_SEARCH'; endLabel: string } | { type: 'LOADING_SETTLED' } | { type: 'RESET' }

const initialState: ExtendedDoctorSearchPhase = { kind: 'idle', extendCount: 0 }

export function extendedSearchReducer(state: ExtendedDoctorSearchPhase, action: Action): ExtendedDoctorSearchPhase {
  switch (action.type) {
    case 'START_SEARCH': {
      return {
        kind: 'searching',
        endLabel: action.endLabel,
        extendCount: state.extendCount,
      }
    }

    case 'LOADING_SETTLED': {
      if (state.kind === 'searching') {
        return {
          kind: 'search-settled',
          endLabel: state.endLabel,
          extendCount: state.extendCount + 1,
        }
      }
      return state
    }

    case 'RESET': {
      return initialState
    }

    default: {
      return state
    }
  }
}

// Hook
// -------------------------------------------------------------------------- /

export function useExtendedDoctorSearch(): {
  phase: ExtendedDoctorSearchPhase
  startSearch: (endLabel: string) => void
  settleSearch: () => void
  reset: () => void
} {
  const [phase, dispatch] = useReducer(extendedSearchReducer, initialState)

  const startSearch = useCallback((endLabel: string) => {
    dispatch({ type: 'START_SEARCH', endLabel })
  }, [])

  const settleSearch = useCallback(() => {
    dispatch({ type: 'LOADING_SETTLED' })
  }, [])

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' })
  }, [])

  return { phase, startSearch, settleSearch, reset }
}
